import { useCallback, useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import { cn } from '@/lib/utils';
import { validateWorkgroupsUploadFileClient } from '@/lib/workgroups-upload-policy';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface WorkgroupsUploadDropZoneProps {
  inputId: string;
  selectedFile: File | null;
  disabled?: boolean;
  onFileSelected: (file: File | null) => void;
  onValidationError: (message: string) => void;
  onMultipleFilesDropped?: () => void;
}

export function WorkgroupsUploadDropZone({
  inputId,
  selectedFile,
  disabled = false,
  onFileSelected,
  onValidationError,
  onMultipleFilesDropped,
}: WorkgroupsUploadDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);
  const [isDragOver, setIsDragOver] = useState(false);

  const applyFile = useCallback(
    (file: File | null) => {
      if (!file) {
        onFileSelected(null);
        return;
      }
      const validationError = validateWorkgroupsUploadFileClient(file);
      if (validationError) {
        onValidationError(validationError);
        onFileSelected(null);
        if (inputRef.current) inputRef.current.value = '';
        return;
      }
      onFileSelected(file);
    },
    [onFileSelected, onValidationError],
  );

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    dragDepthRef.current += 1;
    setIsDragOver(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setIsDragOver(false);
    if (disabled) return;

    const files = event.dataTransfer.files;
    if (files.length === 0) return;
    if (files.length > 1) {
      onMultipleFilesDropped?.();
    }
    applyFile(files[0] ?? null);
  };

  const openFilePicker = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openFilePicker();
    }
  };

  return (
    <div className="space-y-2">
      <div
        data-testid="wg-upload-drop-zone"
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-labelledby={`${inputId}-label`}
        aria-describedby={`${inputId}-hint`}
        onClick={openFilePicker}
        onKeyDown={handleKeyDown}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={cn(
          'flex min-h-[8rem] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          disabled && 'pointer-events-none cursor-not-allowed opacity-60',
          isDragOver
            ? 'border-primary bg-primary/10'
            : 'border-input bg-muted/30 hover:border-primary/50 hover:bg-muted/50',
        )}
      >
        <p id={`${inputId}-label`} className="text-sm font-medium text-foreground">
          Drop a file here or click to browse
        </p>
        <p id={`${inputId}-hint`} className="mt-1 text-xs text-muted-foreground">
          One file per upload · max 75 MB
        </p>
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          className="sr-only"
          disabled={disabled}
          onChange={(e) => applyFile(e.target.files?.[0] ?? null)}
        />
      </div>
      {selectedFile ? (
        <p className="text-xs text-muted-foreground" aria-live="polite">
          Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
        </p>
      ) : null}
    </div>
  );
}
